import React, {useState, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import {Send} from "lucide-react";
import {toLocalDateStr} from "../../../utils/dateHelpers";

const ExportPopup = ({handlePopupToggle, dateRange, startTime, endTime, isDarkMode = false}) => {
    const {t, i18n} = useTranslation("activite");
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [exportOptions, setExportOptions] = useState({
        global: false,
        showCategories: false,
        resume: false,
        detail: false,
        allSalesDetails: false,
        journaux: false
    });
    const [email, setEmail] = useState("");

    // Load email from localStorage on component mount
    useEffect(() => {
        const loadEmail = async () => {
            try {
                const storedEmail = await secureStorage.getItem("_email");
                if (storedEmail) {
                    setEmail(storedEmail);
                }
            } catch (error) {
                console.error("Error loading email:", error);
            }
        };
        loadEmail();
    }, []);

    const handleCheckboxChange = (option) => {
        setExportOptions(prev => ({
            ...prev,
            [option]: !prev[option]
        }));
    };

    const handleExportClick = async () => {
        if (!dateRange[0]?.startDate || !dateRange[0]?.endDate) {
            console.error("Date range is required");
            return;
        }

        setLoading(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const formData = new FormData();
            formData.append("startDate", toLocalDateStr(dateRange[0].startDate));
            formData.append("endDate",   toLocalDateStr(dateRange[0].endDate));
            formData.append("email", email);
            formData.append("startTime", startTime || "00:00");
            formData.append("endTime", endTime || "23:59");

            // Add export options to form data
            formData.append("showCategories", exportOptions.showCategories.toString());
            formData.append("showProducts", exportOptions.showCategories.toString()); // Usually coupled with categories
            formData.append("global", exportOptions.global.toString());
            formData.append("journaux", exportOptions.journaux.toString());
            formData.append("resume", exportOptions.resume.toString());
            formData.append("detail", exportOptions.detail.toString());
            formData.append("allSalesDetails", exportOptions.allSalesDetails.toString());

            const response = await api.post("/report/send_notification", formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            console.log("Export response:", response.data);

            if (response.data.success) {
                setShowSuccess(true);
            }

        } catch (error) {
            console.error("Export error:", error);
            alert(t("exportPopup.exportError"));
        } finally {
            setLoading(false);
        }
    };

    if (showSuccess) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
                <div className={`rounded-lg shadow-lg w-full max-w-[500px] overflow-hidden text-center p-8 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}>
                    <div className="flex justify-center mb-6">
                        <Send
                            size={64}
                            className="text-primary"
                        />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{t("exportPopup.exportInProgress")}</h3>
                    <p className={`mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t("exportPopup.exportInProgressBody")}
                    </p>
                    <p className="text-primary font-medium mb-6">{email}</p>

                    <div className="mt-8">
                        <button
                            className="w-full bg-primary text-white py-4 rounded-b-lg"
                            onClick={() => {
                                setShowSuccess(false);
                                handlePopupToggle();
                            }}
                        >
                            {t("exportPopup.understood")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className={`rounded-lg shadow-lg w-full max-w-[500px] max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white'}`}>

                <div className="px-6 py-4 space-y-3">
                    <h5 className={`text-sm font-semibold mb-4 text-center ${isDarkMode ? 'text-gray-100' : 'text-black'}`}>
                        {t("exportPopup.periodFrom")}{" "}
                        <span className="text-primary">
                            {dateRange[0].startDate.toLocaleDateString(i18n.language)} {startTime}
                        </span>
                        {" "}{t("exportPopup.periodTo")}{" "}
                        <span className="text-primary">
                            {dateRange[0].endDate.toLocaleDateString(i18n.language)}{startTime ? `, ${endTime}` : ""}
                        </span>
                    </h5>

                    <p className={`text-sm mb-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t("exportPopup.linkWillBeSent")}
                    </p>

                    <hr className={`w-1/2 mx-auto my-2 ${isDarkMode ? 'border-gray-600' : ''}`}/>

                    <p className={`text-sm mb-2 text-center ${isDarkMode ? 'text-gray-300' : ''}`}>{email}</p>

                    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                        <div className="mb-4 sm:mb-0 w-full sm:w-1/3">
                            <p className={`font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : ''}`}>{t("exportPopup.report")}</p>
                            <label className={`flex items-center text-sm mb-2 ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    style={{accentColor: '#4DB6AC'}}
                                    checked={exportOptions.global}
                                    onChange={() => handleCheckboxChange('global')}
                                />
                                {t("exportPopup.global")}
                            </label>
                            <label className={`flex items-center text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    style={{accentColor: '#4DB6AC'}}
                                    checked={exportOptions.showCategories}
                                    onChange={() => handleCheckboxChange('showCategories')}
                                />
                                {t("exportPopup.byItemsCategories")}
                            </label>
                        </div>

                        <div className="mb-4 sm:mb-0 w-full sm:w-1/3">
                            <p className={`font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : ''}`}>{t("exportPopup.sales")}</p>
                            <label className={`flex items-center text-sm mb-2 ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    style={{accentColor: '#4DB6AC'}}
                                    checked={exportOptions.resume}
                                    onChange={() => handleCheckboxChange('resume')}
                                />
                                {t("exportPopup.summary")}
                            </label>
                            <label className={`flex items-center text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    style={{accentColor: '#4DB6AC'}}
                                    checked={exportOptions.detail}
                                    onChange={() => handleCheckboxChange('detail')}
                                />
                                {t("exportPopup.detail")}
                            </label>
                            <label className={`flex items-center text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    style={{accentColor: '#4DB6AC'}}
                                    checked={exportOptions.allSalesDetails}
                                    onChange={() => handleCheckboxChange('allSalesDetails')}
                                />
                                {t("exportPopup.allSalesDetails")}
                            </label>
                        </div>

                        <div className="mb-4 sm:mb-0 w-full sm:w-1/3">
                            <p className={`font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : ''}`}>{t("exportPopup.journals")}</p>
                            <label className={`flex items-center text-sm ${isDarkMode ? 'text-gray-300' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    style={{accentColor: '#4DB6AC'}}
                                    checked={exportOptions.journaux}
                                    onChange={() => handleCheckboxChange('journaux')}
                                />
                                {t("exportPopup.salesJournals")}
                            </label>
                        </div>
                    </div>
                </div>
                <div className={`grid grid-cols-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <button
                        className={`py-4 text-[14px] border-l font-light ${isDarkMode ? 'text-gray-300 border-gray-700 hover:bg-gray-700' : 'text-black border-gray-200 hover:bg-gray-50'}`}
                        onClick={handlePopupToggle}
                        disabled={loading}
                    >
                        {t("exportPopup.cancel")}
                    </button>
                    <button
                        className={`py-4 text-primary text-[14px] border-l font-light ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'} ${
                            loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={handleExportClick}
                        disabled={loading}
                    >
                        {loading ? t("exportPopup.exporting") : t("exportPopup.export")}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default ExportPopup;
